import type { ChatThreadWithMessages } from '@/api';
import { getString, parseJsonIfString, toRecord } from '@/utils/jsonHelpers';

/**
 * A thread that was left waiting on the user carries its open interrupt in
 * `pending_interrupt` — but the checkpoint stores whatever LangGraph put there,
 * so it reaches us in one of several shapes: the `{ interrupts: [...] }`
 * envelope the live socket sends, a bare array, a single `{ id, value }`
 * record, one nested under `interrupt`, or any of those as a JSON string.
 *
 * `normalizeInterruptPayload` only understands the envelope, so restoring a
 * transcript has to funnel the rest into it first — otherwise reopening a
 * thread from History silently drops the very question that is blocking it.
 */
function toInterruptEnvelope(pendingInterrupt: unknown): unknown {
  const parsed = parseJsonIfString(pendingInterrupt);
  if (!parsed) return null;

  if (Array.isArray(parsed)) return { type: 'interrupt', interrupts: parsed };

  const record = toRecord(parsed);
  if (!record) return null;

  if (Array.isArray(record.interrupts)) {
    return {
      ...record,
      type: getString(record, ['type']) ?? 'interrupt',
      interrupts: record.interrupts,
    };
  }

  if (toRecord(record.value) && getString(record, ['id'])) {
    return { type: 'interrupt', interrupts: [record] };
  }

  const nested = toRecord(record.interrupt);
  if (nested && toRecord(nested.value) && getString(nested, ['id'])) {
    return { type: 'interrupt', interrupts: [nested] };
  }

  return null;
}

/** The thread's open interrupt, in the envelope `normalizeInterruptPayload` takes. */
export function extractPendingInterrupt(thread: ChatThreadWithMessages): unknown {
  return (
    toInterruptEnvelope(thread.pending_interrupt) ??
    // Older checkpoints stashed it in the metadata bag instead.
    toInterruptEnvelope(toRecord(thread.thread_metadata)?.pending_interrupt)
  );
}
